import * as React from 'react';
import {useCallback, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Paper} from '@mui/material';
import Button from '@mui/material/Button';
import {useAppTranslation} from 'services/i18n';
import {useAppDispatch} from 'store';
import {cancelUserApprovalRequest} from 'store/approvals';
import {addMessage} from 'store/localApp';
import {isApiResultError} from '../../helpers/api';
import {approvalSubjectLabel} from 'model/approval';

/**
 * Withdrawing one's own pending request. Cancellation is final -- the record
 * stays on record but can never be approved or reopened -- so it is confirmed
 * rather than fired straight from the menu.
 */
export const ApprovalCancelDialog = ({item, userId, onSuccess, onClose}: {
    item: {id: number, subject_title?: string | null, subject_content?: string | null},
    userId: number,
    onSuccess: () => void,
    onClose: () => void,
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);

    const handleConfirm = useCallback(async () => {
        setIsSaving(true);
        const result = await dispatch(cancelUserApprovalRequest({userId, id: String(item.id)}));
        setIsSaving(false);

        if (isApiResultError(result)) {
            // apiErrorMiddleware already surfaces the server's own explanation
            return;
        }
        dispatch(addMessage({severity: 'success', title: t('approvals.cancel.done')}));
        onSuccess();
        onClose();
    }, [item.id, userId, dispatch, onSuccess, onClose, t]);

    return <Dialog open={true} onClose={onClose} PaperComponent={Paper} fullWidth maxWidth={'sm'}>
        <DialogTitle>{t('approvals.cancel.title', {subject: approvalSubjectLabel(item)})}</DialogTitle>
        <LinearProgress hidden={!isSaving}/>
        <DialogContent>
            <DialogContentText>{t('approvals.cancel.text')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{padding: '16px'}}>
            <Button color={'inherit'} onClick={onClose} disabled={isSaving}>{t('form.cancel')}</Button>
            <Button variant={'contained'} onClick={handleConfirm} disabled={isSaving}
                sx={{
                    background: 'var(--color-error-back)',
                    color: '#A32409',
                    '&:hover': {background: '#FBD5CE'},
                }}>
                {t('approvals.cancel.confirm')}
            </Button>
        </DialogActions>
    </Dialog>;
};
